import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";
import "./SlideOutPanel.css";

/**
 * Высокая панель, выезжающая слева от боковой панели поверх 3D. Сюда выносится
 * всё, где нужно видеть много вариантов сразу: каталог декоров и настройка
 * ячеек стеллажа. Одновременно открыта только одна такая панель.
 */
interface SlideOutState {
  owner: string | null;
  /** Ширина открытой панели, px: 3D сдвигается влево, чтобы изделие не пряталось под ней. */
  inset: number;
  /** Выбор ячейки без панели (тянут полку, открывают ящик) — время отметки. */
  quietAt: number;
  open: (owner: string) => void;
  close: (owner: string) => void;
  /** Следующий выбор ячейки не открывает панель. */
  markQuiet: () => void;
  /** Был ли только что «тихий» выбор; отметка снимается. */
  takeQuiet: () => boolean;
}

export const useSlideOut = create<SlideOutState>((set, get) => ({
  owner: null,
  inset: 0,
  quietAt: 0,
  open: owner => set({ owner }),
  close: owner => { if (get().owner === owner) set({ owner: null }); },
  markQuiet: () => set({ quietAt: Date.now() }),
  takeQuiet: () => {
    const quiet = Date.now() - get().quietAt < 800;
    if (get().quietAt) set({ quietAt: 0 });
    return quiet;
  }
}));

export const SLIDE_OUT_ROOT_ID = "slideout-root";

export function SlideOutPanel({ owner, eyebrow, title, onClose, children, footer }: {
  owner: string;
  eyebrow?: string;
  title: string;
  onClose?: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const active = useSlideOut(state => state.owner === owner);
  const close = useSlideOut(state => state.close);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const panelRef = useRef<HTMLElement>(null);

  // Ширина панели — для сдвига 3D. На телефоне панель во весь экран, сдвигать нечего.
  useEffect(() => {
    const panel = panelRef.current;
    if (!active || !panel) return;
    const publish = () => useSlideOut.setState({ inset: panel.getBoundingClientRect().width });
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(panel);
    return () => { observer.disconnect(); useSlideOut.setState({ inset: 0 }); };
  }, [active, host]);

  useEffect(() => {
    setHost(document.getElementById(SLIDE_OUT_ROOT_ID));
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      close(owner);
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, owner, close, onClose]);

  if (!active || !host) return null;
  return createPortal(
    <aside ref={panelRef} className="slideout" role="dialog" aria-label={title} data-owner={owner}>
      <header className="slideout__header">
        <div>
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          <h2>{title}</h2>
        </div>
        <button type="button" className="slideout__close" aria-label="Закрыть панель"
          onClick={() => { close(owner); onClose?.(); }}>×</button>
      </header>
      <div className="slideout__body">{children}</div>
      {footer && <footer className="slideout__footer">{footer}</footer>}
    </aside>,
    host
  );
}
